<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

import arrowRightIcon from '@clovyr/bed/assets/images/icons/ai-landing/arrow-right.svg';
import shieldOutlineIcon from '@clovyr/bed/assets/images/icons/ai-landing/shield-outline.svg';
import sparklesFillIcon from '@clovyr/bed/assets/images/icons/ai-landing/sparkles-fill.svg';
import zapOutlineIcon from '@clovyr/bed/assets/images/icons/ai-landing/zap-outline.svg';
import BaseSvg from '@clovyr/bed/components/icons/BaseSvg.vue';
import type { Manifest } from '@clovyr/pollen/manifest/manifest';

import { useLaunchButton } from '@/composables/useLaunchButton';

import logoUrl from '../../../assets/images/ai-landing/clovyr-icon-1.svg';
import landingImage from '../../../assets/images/ai-landing/landing-lg.jpg';

import ImageWithFallback from './ImageWithFallback.vue';

const props = defineProps<{
  app: Manifest;
  onLaunch?: () => void;
  launcher?: boolean;
}>();

const { handleLaunchClicked } = useLaunchButton(props.app);

const launchFromHero = () => {
  if (props.onLaunch) {
    props.onLaunch();
    return;
  }

  handleLaunchClicked();
};

type ChatMessage = {
  role: 'user' | 'ai';
  text: string;
};

type CheckLabel = {
  text: string;
  iconSrc: string;
};

const chatMessages: ChatMessage[] = [
  { role: 'user', text: 'Summarize our Q4 product roadmap doc.' },
  {
    role: 'ai',
    text: 'Sure! Based on your uploaded roadmap, here are the top 3 priorities for Q4: ship the multi-model router, launch team workspaces, and complete the SOC 2 audit. Want a deeper breakdown?',
  },
  { role: 'user', text: 'Which LLM gave that answer?' },
  {
    role: 'ai',
    text: 'That was Llama 3 running locally on your own instance no data left your infrastructure.',
  },
];

const checkLabels: CheckLabel[] = [
  { text: 'Zero data leakage', iconSrc: shieldOutlineIcon },
  { text: 'Any LLM, any doc', iconSrc: sparklesFillIcon },
  { text: '1-click launch', iconSrc: zapOutlineIcon },
];
const bounceDelays = [0, 150, 300];
const firstMessageDelay = 900;
const perMessageDelay = 700;
const loopPause = 2200;
const fadeOutDuration = 450;
const restartPause = 500;

const visibleMessages = ref(0);
const isResettingChat = ref(false);

let revealTimer: ReturnType<typeof setTimeout> | undefined;

const scheduleNextMessage = () => {
  if (visibleMessages.value >= chatMessages.length) {
    revealTimer = setTimeout(() => {
      isResettingChat.value = true;
      revealTimer = setTimeout(() => {
        visibleMessages.value = 0;
        isResettingChat.value = false;
        revealTimer = setTimeout(() => {
          visibleMessages.value = 1;
          scheduleNextMessage();
        }, restartPause);
      }, fadeOutDuration);
    }, loopPause);
    return;
  }

  revealTimer = setTimeout(
    () => {
      visibleMessages.value += 1;
      scheduleNextMessage();
    },
    firstMessageDelay + visibleMessages.value * perMessageDelay,
  );
};

onMounted(() => {
  watch(
    () => props.launcher,
    (launcher) => {
      if (revealTimer) {
        clearTimeout(revealTimer);
      }
      if (launcher) {
        return;
      }
      visibleMessages.value = 0;
      isResettingChat.value = false;
      scheduleNextMessage();
    },
    { immediate: true },
  );
});

onBeforeUnmount(() => {
  if (revealTimer) {
    clearTimeout(revealTimer);
  }
});
</script>

<template>
  <section class="ai-hero" :class="{ 'ai-hero--launcher': launcher }">
    <div class="ai-hero__photo-layer">
      <ImageWithFallback :src="landingImage" alt="" aria-hidden="true" class="ai-hero__photo" />
    </div>

    <div class="ai-hero__ambient-layer">
      <div class="ai-hero__blob ai-hero__blob--green"></div>
      <div class="ai-hero__blob ai-hero__blob--violet"></div>
      <div class="ai-hero__blob ai-hero__blob--blue"></div>
    </div>

    <div class="ai-hero__grid-overlay"></div>

    <div class="ai-hero__topbar">
      <div class="ai-hero__brand">
        <img :src="logoUrl" alt="Clovyr logo" class="ai-hero__logo" />
        <span class="ai-hero__brand-text">Clovyr <span>AI Cloud</span></span>
      </div>
    </div>

    <template v-if="!launcher">
      <div class="ai-hero__content">
        <div class="ai-hero__left">
          <h1 class="ai-hero__heading">
            Bring your own models,
            <span> keep your own data.</span>
          </h1>

          <p class="ai-hero__copy">
            Clovyr AI Cloud lets you chat with your documents privately and choose the LLMs you
            prefer, local or cloud. Privacy, security, and choice, without compromising convenience.
          </p>

          <div class="ai-hero__actions">
            <button type="button" class="ai-hero__cta" @click="launchFromHero">
              <span>Launch Clovyr AI Cloud</span>
              <BaseSvg class="ai-hero__cta-icon" viewBox="0 0 24 24" aria-hidden="true" role="img">
                <image
                  :href="arrowRightIcon"
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMidYMid meet"
                />
              </BaseSvg>
            </button>
          </div>

          <div class="ai-hero__checks">
            <div v-for="item in checkLabels" :key="item.text" class="ai-hero__check">
              <span class="ai-hero__check-icon-wrap">
                <BaseSvg
                  class="ai-hero__check-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <image
                    :href="item.iconSrc"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </BaseSvg>
              </span>
              {{ item.text }}
            </div>
          </div>
        </div>

        <div class="ai-hero__right">
          <div class="ai-chat__panel-glow"></div>

          <div class="ai-chat">
            <div class="ai-chat__chrome">
              <div class="ai-chat__lights">
                <span class="ai-chat__light ai-chat__light--red"></span>
                <span class="ai-chat__light ai-chat__light--yellow"></span>
                <span class="ai-chat__light ai-chat__light--green"></span>
              </div>

              <div class="ai-chat__status">
                <BaseSvg
                  class="ai-chat__status-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <image
                    :href="shieldOutlineIcon"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </BaseSvg>
                <span>private · clovyr cloud · llama3-8b</span>
              </div>

              <div class="ai-chat__chrome-spacer"></div>
            </div>

            <div class="ai-chat__workspace-label-wrap">
              <div class="ai-chat__workspace-label">Q4 Strategy Workspace</div>
            </div>

            <div
              class="ai-chat__messages"
              :class="{ 'ai-chat__messages--resetting': isResettingChat }"
            >
              <TransitionGroup name="ai-chat-message" tag="div" class="ai-chat__message-list">
                <div
                  v-for="(msg, index) in chatMessages.slice(0, visibleMessages)"
                  :key="`${msg.role}-${index}`"
                  class="ai-chat__row"
                  :class="{ 'ai-chat__row--user': msg.role === 'user' }"
                >
                  <div v-if="msg.role === 'ai'" class="ai-chat__avatar ai-chat__avatar--ai">
                    <BaseSvg
                      class="ai-chat__sparkles-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      role="img"
                    >
                      <image
                        :href="sparklesFillIcon"
                        x="0"
                        y="0"
                        width="100%"
                        height="100%"
                        preserveAspectRatio="xMidYMid meet"
                      />
                    </BaseSvg>
                  </div>

                  <div
                    class="ai-chat__bubble"
                    :class="{
                      'ai-chat__bubble--user': msg.role === 'user',
                      'ai-chat__bubble--ai': msg.role === 'ai',
                    }"
                  >
                    {{ msg.text }}
                  </div>

                  <div v-if="msg.role === 'user'" class="ai-chat__avatar ai-chat__avatar--user">
                    U
                  </div>
                </div>
              </TransitionGroup>

              <div
                v-if="
                  visibleMessages < chatMessages.length && visibleMessages > 0 && !isResettingChat
                "
                class="ai-chat__typing"
              >
                <span
                  v-for="delay in bounceDelays"
                  :key="delay"
                  class="ai-chat__typing-dot"
                  :style="{ animationDelay: `${delay}ms` }"
                ></span>
              </div>
            </div>

            <div class="ai-chat__input-wrap">
              <div class="ai-chat__input-row">
                <input
                  class="ai-chat__input"
                  placeholder="Ask anything about your documents..."
                  readonly
                />

                <div class="ai-chat__send">
                  <BaseSvg
                    class="ai-chat__send-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    role="img"
                  >
                    <image
                      :href="arrowRightIcon"
                      x="0"
                      y="0"
                      width="100%"
                      height="100%"
                      preserveAspectRatio="xMidYMid meet"
                    />
                  </BaseSvg>
                </div>
              </div>

              <div class="ai-chat__meta">
                <span>4 documents · RAG enabled</span>
                <span class="ai-chat__meta-right">on-premise</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="ai-hero__scroll">
        <span class="ai-hero__scroll-label">scroll</span>
        <div class="ai-hero__scroll-ring">
          <span class="ai-hero__scroll-dot"></span>
        </div>
      </div>
    </template>

    <div v-else class="ai-hero__launcher">
      <slot name="launcher" />
    </div>
  </section>
</template>

<style scoped lang="scss">
.ai-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #221a33;
  color: #fff;
}

.ai-hero__photo-layer,
.ai-hero__ambient-layer,
.ai-hero__grid-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/*
 * In launcher/wizard mode, `.ai-hero`'s height grows or shrinks with whichever
 * wizard step is mounted (Setup/Host/Configure Host/Billing/Review). Anchoring
 * these layers to the viewport instead of `.ai-hero`'s box keeps the
 * background artwork at a fixed scale/position no matter which step is showing.
 */
.ai-hero--launcher .ai-hero__photo-layer,
.ai-hero--launcher .ai-hero__ambient-layer,
.ai-hero--launcher .ai-hero__grid-overlay {
  position: fixed;
  inset: 0;
  z-index: 0;
}

.ai-hero__photo {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 60%;
  object-fit: cover;
  object-position: left;
  opacity: 0.12;
  mask-image: linear-gradient(to right, transparent 0%, black 40%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 40%);
}

.ai-hero__blob {
  position: absolute;
  border-radius: 999px;
}

.ai-hero__blob--green {
  top: -10%;
  left: 10%;
  width: 600px;
  height: 600px;
  background: rgba(0, 191, 116, 0.08);
  filter: blur(120px);
}

.ai-hero__blob--violet {
  top: 20%;
  right: -5%;
  width: 500px;
  height: 500px;
  background: rgba(124, 58, 237, 0.15);
  filter: blur(120px);
}

.ai-hero__blob--blue {
  bottom: -10%;
  left: 30%;
  width: 400px;
  height: 400px;
  background: rgba(14, 165, 233, 0.08);
  filter: blur(100px);
}

.ai-hero__grid-overlay {
  opacity: 0.04;
  background-image:
    linear-gradient(#00bf74 1px, transparent 1px),
    linear-gradient(90deg, #00bf74 1px, transparent 1px);
  background-size: 60px 60px;
}

.ai-hero__topbar {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 0;
}

.ai-hero__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ai-hero__logo {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
}

.ai-hero__brand-text {
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.ai-hero__brand-text span {
  color: #00bf74;
}

.ai-hero__content {
  position: relative;
  z-index: 10;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem 6rem;
}

.ai-hero__launcher {
  position: relative;
  z-index: 10;
  display: flex;
  flex: 1;
  width: 100%;
}

.ai-hero__left {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2rem;
}

.ai-hero__heading {
  margin: 0;
  color: #fff;
  font-size: 3.6rem;
  line-height: 1.05;
  letter-spacing: -0.025em;
  font-weight: 900;
  opacity: 0;
  transform: translateY(24px);
  animation: heroFadeUp 0.7s ease-out 0.1s forwards;
}

.ai-hero__heading span {
  background: linear-gradient(to right, #00bf74, #00e874);
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
}

.ai-hero__copy {
  margin: 0;
  color: #9ca3af;
  font-size: 1.125rem;
  line-height: 1.625;
  opacity: 0;
  transform: translateY(20px);
  animation: heroFadeUp 0.6s ease-out 0.25s forwards;
}

.ai-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  opacity: 0;
  transform: translateY(16px);
  animation: heroFadeUp 0.6s ease-out 0.4s forwards;
}

.ai-hero__cta {
  appearance: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  align-self: flex-start;
  border-radius: 0.75rem;
  background: #00bf74;
  color: #000;
  font-weight: 600;
  text-decoration: none;
  padding: 0.875rem 1.75rem;
  transition: background-color 200ms ease;
}

.ai-hero__cta:hover {
  background: #00e874;
}

.ai-hero__cta-icon {
  width: 1rem;
  height: 1rem;
}

.ai-hero__checks {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding-top: 0.5rem;
  flex-wrap: wrap;
  opacity: 0;
  animation: heroFadeIn 0.35s linear 0.7s forwards;
}

.ai-hero__check {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.ai-hero__check-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #00bf74;
}

.ai-hero__check-icon {
  width: 0.875rem;
  height: 0.875rem;
}

.ai-hero__right {
  position: relative;
  width: 100%;
  max-width: 480px;
  flex: 1;
  opacity: 0;
  transform: translateX(40px) scale(0.97);
  animation: heroSlidePanel 0.8s ease-out 0.3s forwards;
}

.ai-chat__panel-glow {
  position: absolute;
  inset: 0;
  border-radius: 1rem;
  background: linear-gradient(to bottom right, rgba(0, 191, 116, 0.1), rgba(124, 58, 237, 0.1));
  filter: blur(32px);
  transform: scale(1.05);
}

.ai-chat {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #1a1230;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.35);
}

.ai-chat__chrome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: #150f27;
}

.ai-chat__lights {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-chat__light {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 999px;
}

.ai-chat__light--red {
  background: rgba(239, 68, 68, 0.6);
}

.ai-chat__light--yellow {
  background: rgba(234, 179, 8, 0.6);
}

.ai-chat__light--green {
  background: rgba(0, 191, 116, 0.6);
}

.ai-chat__status {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 0.375rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.25rem 0.75rem;
}

.ai-chat__status span {
  color: #6b7280;
  font-size: 10px;
}

.ai-chat__status-icon {
  width: 11px;
  height: 11px;
  color: #00bf74;
}

.ai-chat__chrome-spacer {
  width: 4rem;
}

.ai-chat__workspace-label-wrap {
  padding: 0.75rem 1rem 0.25rem;
}

.ai-chat__workspace-label {
  color: #4b5563;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.ai-chat__messages {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 280px;
  padding: 0.5rem 1rem;
  transition: opacity 450ms ease;
}

.ai-chat__messages--resetting {
  opacity: 0;
}

.ai-chat__message-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ai-chat__row {
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
}

.ai-chat__row--user {
  justify-content: flex-end;
}

.ai-chat__avatar {
  display: flex;
  width: 1.75rem;
  height: 1.75rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
}

.ai-chat__avatar--ai {
  background: linear-gradient(to bottom right, #00bf74, #0ea5e9);
  color: #000;
}

.ai-chat__avatar--user {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #2e2248;
  color: #9ca3af;
  font-size: 0.75rem;
}

.ai-chat__sparkles-icon {
  width: 12px;
  height: 12px;
}

.ai-chat__bubble {
  max-width: 80%;
  padding: 0.5rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  line-height: 1.625;
}

.ai-chat__bubble--user {
  border-top-right-radius: 0.125rem;
  background: #2e2248;
  color: #e5e7eb;
}

.ai-chat__bubble--ai {
  border: 1px solid rgba(0, 191, 116, 0.2);
  border-top-left-radius: 0.125rem;
  background: #1e1a30;
  color: #e5e7eb;
}

.ai-chat__typing {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding-left: 2.25rem;
}

.ai-chat__typing-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 999px;
  background: #00bf74;
  animation: typingBounce 1s infinite;
}

.ai-chat-message-enter-active {
  transition:
    opacity 0.5s ease-out,
    transform 0.5s ease-out;
}

.ai-chat-message-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.ai-chat-message-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.ai-chat__input-wrap {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: #150f27;
  padding: 0.75rem 1rem;
}

.ai-chat__input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  background: #1e1538;
  padding: 0.625rem 0.75rem;
}

.ai-chat__input {
  flex: 1;
  border: 0;
  background: transparent;
  color: #9ca3af;
  font-size: 0.75rem;
  outline: none;
}

.ai-chat__input::placeholder {
  color: #4b5563;
}

.ai-chat__send {
  display: flex;
  width: 1.5rem;
  height: 1.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: #00bf74;
  color: #000;
}

.ai-chat__send-icon {
  width: 12px;
  height: 12px;
}

.ai-chat__meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding: 0 0.25rem;
  color: #4b5563;
  font-size: 10px;
}

.ai-chat__meta-right {
  margin-left: auto;
  color: rgba(0, 191, 116, 0.6);
}

.ai-hero__scroll {
  position: absolute;
  left: 50%;
  bottom: 2rem;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  transform: translateX(-50%);
  opacity: 0;
  animation: heroFadeIn 0.35s linear 1.5s forwards;
}

.ai-hero__scroll-label {
  color: #4b5563;
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
}

.ai-hero__scroll-ring {
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #374151;
  border-radius: 999px;
  animation: scrollBob 1.6s ease-in-out infinite;
}

.ai-hero__scroll-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 999px;
  background: #00bf74;
}

@media (min-width: 640px) {
  .ai-hero__actions {
    flex-direction: row;
  }
}

@media (min-width: 1024px) {
  .ai-hero__content {
    flex-direction: row;
    align-items: center;
    gap: 4rem;
    padding-left: 5rem;
    padding-right: 5rem;
  }

  .ai-hero__left {
    max-width: 560px;
  }

  .ai-hero__heading {
    font-size: 4.5rem;
  }
}

@media (max-width: 1279px) {
  .ai-hero__content {
    gap: 3rem;
    padding-top: 3.5rem;
    padding-bottom: 5.25rem;
  }

  .ai-hero__heading {
    font-size: clamp(3.35rem, 7.5vw, 4.1rem);
  }

  .ai-hero__right {
    max-width: 540px;
  }
}

@media (max-width: 1023px) {
  .ai-hero__left {
    align-items: center;
    text-align: center;
  }

  .ai-hero__actions {
    justify-content: center;
  }

  .ai-hero__cta {
    align-self: center;
  }

  .ai-hero__checks {
    justify-content: center;
  }

  .ai-hero__copy {
    max-width: 50rem;
  }

  .ai-hero__right {
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 900px) {
  .ai-hero__photo {
    width: 100%;
    mask-image: linear-gradient(to bottom, black 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%);
    -webkit-mask-image: linear-gradient(
      to bottom,
      black 0%,
      rgba(0, 0, 0, 0.2) 50%,
      transparent 100%
    );
  }

  .ai-hero__topbar {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .ai-hero__content {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .ai-hero__heading {
    font-size: clamp(2.7rem, 10vw, 3.6rem);
  }
}

@media (max-width: 767px) {
  .ai-hero {
    min-height: auto;
  }

  .ai-hero__topbar {
    padding-top: 1.5rem;
  }

  .ai-hero__logo {
    width: 2.5rem;
    height: 2.5rem;
  }

  .ai-hero__brand-text {
    font-size: 1.55rem;
  }

  .ai-hero__content {
    gap: 2.25rem;
    padding-top: 2.5rem;
    padding-bottom: 5rem;
  }

  .ai-hero__left {
    gap: 1.5rem;
  }

  .ai-hero__copy {
    font-size: 1rem;
    line-height: 1.55;
  }

  .ai-hero__checks {
    flex-direction: column;
    align-items: center;
    gap: 0.7rem;
    padding-top: 0;
  }

  .ai-chat__messages {
    min-height: 230px;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .ai-chat__workspace-label-wrap,
  .ai-chat__chrome,
  .ai-chat__input-wrap {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .ai-chat__meta {
    gap: 0.5rem;
  }

  .ai-hero__scroll {
    display: none;
  }
}

@media (max-width: 520px) {
  .ai-hero__topbar,
  .ai-hero__content {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .ai-hero__logo {
    width: 2rem;
    height: 2rem;
  }

  .ai-hero__brand-text {
    font-size: 1.22rem;
  }

  .ai-hero__heading {
    font-size: clamp(2.2rem, 12vw, 2.8rem);
    line-height: 1.08;
  }

  .ai-hero__copy {
    font-size: 0.95rem;
  }

  .ai-hero__cta {
    width: 100%;
    padding: 0.875rem 1rem;
  }

  .ai-chat__chrome {
    gap: 0.4rem;
  }

  .ai-chat__status {
    padding: 0.2rem 0.45rem;
  }

  .ai-chat__status span {
    font-size: 9px;
  }

  .ai-chat__chrome-spacer {
    width: 0;
  }

  .ai-chat__bubble {
    max-width: 88%;
    font-size: 0.72rem;
  }
}

@keyframes heroFadeUp {
  from {
    opacity: 0;
    transform: translateY(var(--hero-shift, 24px));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes heroFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes heroSlidePanel {
  from {
    opacity: 0;
    transform: translateX(40px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes typingBounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
}

@keyframes scrollBob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(6px);
  }
}
</style>
